import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { supplierOrderNeedsAPI } from '../../services/purchaseOrder';
import { branchAPI } from '../../services/branch';

const fmtQty = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtMoney = (n) => Number(n ?? 0).toLocaleString('vi-VN') + 'đ';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('vi-VN') : '—');
const pkgLabel = (pkg, unit) => pkg?.unit_label || pkg?.name || unit || '';

/** Nhãn trạng thái đơn — dùng ĐÚNG PurchaseOrderStatus sẵn có, không đẻ trạng thái riêng. */
const ORDER_STATUS = {
  DRAFT: { text: 'Nháp — CHƯA đặt', bg: 'secondary' },
  CONFIRMED: { text: 'Đã đặt', bg: 'success' },
  RECEIVING: { text: 'Đang về', bg: 'info' },
  COMPLETED: { text: 'Đã về', bg: 'dark' },
  CANCELLED: { text: 'Đã huỷ', bg: 'light' },
};

/**
 * #002620 — "NCC ĐANG CẦN ĐẶT HÀNG".
 *
 * Gom NVL dưới ngưỡng tối thiểu THEO NHÀ CUNG CẤP, và MỖI KÊNH ĐẶT THAO TÁC MỘT KIỂU
 * (lệnh sếp 731599116 + 731599117). Trang này KHÔNG tự đặt hàng: mỗi nút chỉ dẫn sang đúng luồng
 * đã chạy sẵn — Zalo sang màn "Gửi đơn qua Zalo" (#233007), Shopee sang màn "Cần mua qua Shopee"
 * (#233654), gọi điện thì bấm số.
 *
 * Kênh đặt được SUY RA từ dữ liệu thật của NCC (SĐT Zalo / link Shopee / SĐT) — xem giải trình ở
 * `supplier-order-channel.util.ts`. NCC không có gì thì hiện thẳng "chưa khai cách đặt", CẤM đoán.
 *
 * Thiết kế cho ĐIỆN THOẠI trước (khổ 390×844): mỗi NCC là 1 thẻ, nút thao tác to.
 */
export default function SupplierOrderNeedsPage() {
  const navigate = useNavigate();
  const { branches, selectedBranch } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();

  /** '' = TẤT CẢ chi nhánh. Link từ tin Telegram (#002620) truyền `?branch=<id>` để lọc sẵn. */
  const [branchId, setBranchId] = useState(searchParams.get('branch') || '');
  const [branchTouched, setBranchTouched] = useState(!!searchParams.get('branch'));
  const [allBranches, setAllBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [busySupplier, setBusySupplier] = useState(null);
  /** SL sửa tay trước khi đánh dấu đã đặt: { `${supplier_id}|${branch_id}|${material_id}`: chuỗi } */
  const [qtyDraft, setQtyDraft] = useState({});

  /**
   * Nguồn chi nhánh: đọc thẳng `/branches/active/simple` (đúng tiền lệ #125114/#233654) chứ KHÔNG
   * dùng `useBranch().branches` — danh sách trong context bị bó theo chi nhánh người dùng đang
   * đứng nên không lọc được đủ Q10 / Bình Thạnh / Thủ Đức.
   */
  useEffect(() => {
    let alive = true;
    branchAPI.getActiveSimple()
      .then((res) => {
        if (!alive) return;
        setAllBranches(Array.isArray(res) ? res : res?.data || []);
      })
      .catch(() => { /* rơi về danh sách trong context */ });
    return () => { alive = false; };
  }, []);

  const branchOptions = useMemo(
    () => (allBranches.length ? allBranches : (branches || [])),
    [allBranches, branches],
  );

  // Vào thẳng từ menu (không có ?branch=) → mặc định chi nhánh đang chọn, vẫn đổi sang "tất cả" được.
  useEffect(() => {
    if (branchTouched || !branchOptions.length) return;
    setBranchTouched(true);
    if (selectedBranch?.id && branchOptions.some((b) => b.id === selectedBranch.id)) {
      setBranchId(selectedBranch.id);
    }
  }, [branchOptions, selectedBranch, branchTouched]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await supplierOrderNeedsAPI.getNeeds(branchId || undefined);
      setData(res);
      setQtyDraft({});
    } catch (err) {
      setError(err?.message || 'Không tải được danh sách NCC cần đặt hàng');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const onChangeBranch = (value) => {
    setBranchId(value);
    setBranchTouched(true);
    // Giữ bộ lọc trên thanh địa chỉ để link gửi lại cho người khác vẫn ra đúng màn hình đang xem.
    const next = new URLSearchParams(searchParams);
    if (value) next.set('branch', value); else next.delete('branch');
    setSearchParams(next, { replace: true });
  };

  const matches = useCallback(
    (text) => !search || (text || '').toLowerCase().includes(search.toLowerCase()),
    [search],
  );

  /** Lọc ô tìm kiếm: khớp TÊN NCC thì giữ cả thẻ; không thì giữ NCC có NVL khớp. */
  const suppliers = useMemo(() => {
    const list = data?.suppliers || [];
    if (!search) return list;
    return list
      .map((s) => (matches(s.supplier_name)
        ? s
        : { ...s, items: s.items.filter((i) => matches(i.material_name)) }))
      .filter((s) => s.items.length);
  }, [data, search, matches]);

  const unassigned = useMemo(
    () => (data?.unassigned || []).filter((i) => matches(i.material_name)),
    [data, matches],
  );

  const qtyOf = (key, suggested) => {
    const d = qtyDraft[key];
    if (d !== undefined) return d;
    return suggested === null || suggested === undefined ? '' : String(suggested);
  };

  // -------------------------------------------------------------------------
  // Thao tác theo KÊNH — mỗi kênh một đường, không kênh nào tự đặt hàng hộ
  // -------------------------------------------------------------------------

  const goZalo = (supplier) => {
    // Màn "Gửi đơn qua Zalo" (#233007) đã nhận sẵn ?supplier_id — đi đúng luồng đang chạy
    // production, KHÔNG dựng luồng gửi Zalo thứ hai ở đây.
    navigate(`/inventory/supplier-order-zalo?supplier_id=${encodeURIComponent(supplier.supplier_id)}`);
  };

  const goShopee = () => {
    // Màn "Cần mua qua Shopee" (#233654) làm việc theo 1 chi nhánh. Đang xem "tất cả" thì chưa
    // biết mở chi nhánh nào — nói thẳng thay vì tự chọn bừa một chi nhánh.
    if (!branchId) {
      toast.info('Danh sách Shopee làm việc theo từng chi nhánh — hãy chọn 1 chi nhánh trước.');
      return;
    }
    navigate(`/inventory/shopee-shopping?branch=${encodeURIComponent(branchId)}`);
  };

  const markOrdered = async (supplier, branch) => {
    const items = supplier.items.filter((i) => i.branch_id === branch.id);
    const payload = [];
    for (const it of items) {
      const raw = qtyOf(`${supplier.supplier_id}|${it.branch_id}|${it.material_id}`, it.suggested_packages);
      const qty = Number(raw);
      // NVL chưa có định mức → không có số gợi ý. Bỏ qua dòng đó thay vì tự bịa số 1.
      if (!raw || !Number.isFinite(qty) || qty <= 0) continue;
      payload.push({ material_id: it.material_id, package_quantity: qty });
    }
    if (!payload.length) {
      toast.warning('Chưa có dòng nào có số lượng hợp lệ — hãy nhập số lượng trước khi đánh dấu đã đặt.');
      return;
    }

    const key = `${supplier.supplier_id}|${branch.id}`;
    try {
      setBusySupplier(key);
      const po = await supplierOrderNeedsAPI.markOrdered({
        supplier_id: supplier.supplier_id,
        branch_id: branch.id,
        items: payload,
      });
      toast.success(`Đã tạo đơn ${po?.order_number || ''} (trạng thái Đã đặt) cho ${supplier.supplier_name}`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Không đánh dấu được là đã đặt');
    } finally {
      setBusySupplier(null);
    }
  };

  // -------------------------------------------------------------------------

  const renderChannelActions = (supplier) => {
    if (!supplier.channels.length) {
      return (
        <Alert variant="warning" className="mb-2 py-2 px-3 small">
          <div className="fw-bold">⚠️ NCC này CHƯA KHAI CÁCH ĐẶT</div>
          <div className="mb-2">
            Chưa có SĐT Zalo, chưa có SĐT liên hệ, và chưa NVL nào của NCC này gắn link Shopee —
            hệ thống KHÔNG đoán giúp cách đặt.
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" variant="warning" onClick={() => navigate('/inventory/suppliers')}>
              Khai SĐT / Zalo cho NCC
            </Button>
            <Button size="sm" variant="outline-warning" onClick={goShopee}>
              Gắn link Shopee
            </Button>
          </div>
        </Alert>
      );
    }

    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        {supplier.channels.map((ch) => {
          if (ch.kind === 'ZALO') {
            return (
              <Button key="zalo" size="sm" variant="primary" onClick={() => goZalo(supplier)}>
                💬 Gửi đơn qua Zalo
                <span className="d-block small opacity-75">{ch.phone}</span>
              </Button>
            );
          }
          if (ch.kind === 'SHOPEE') {
            return (
              <Button key="shopee" size="sm" variant="danger" onClick={goShopee}>
                🛒 Mở danh sách mua trên Shopee
                <span className="d-block small opacity-75">
                  {ch.linked_material_count} NVL đã có link
                </span>
              </Button>
            );
          }
          return (
            // Kênh gọi điện: `tel:` để bấm là gọi thật trên điện thoại.
            <Button key="phone" size="sm" variant="success" href={`tel:${ch.phone}`}>
              📞 Gọi {ch.phone}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderSupplier = (supplier) => {
    const open = expanded[supplier.supplier_id];
    const hasPhoneChannel = supplier.channels.some((c) => c.kind === 'PHONE');
    const noChannel = supplier.channels.length === 0;

    return (
      <Card key={supplier.supplier_id} className="mb-3 shadow-sm">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="flex-grow-1">
              <div className="fw-bold fs-6">{supplier.supplier_name}</div>
              <div className="small text-muted">
                {supplier.channels.length
                  ? supplier.channels.map((c) => c.label).join(' · ')
                  : 'Chưa khai cách đặt'}
              </div>
            </div>
            <div className="text-end">
              <Badge bg="danger">{supplier.item_count} món</Badge>
              <div className="small fw-bold mt-1">{fmtMoney(supplier.estimated_total)}</div>
              {supplier.unpriced_count > 0 && (
                <div className="small text-warning">
                  chưa tính {supplier.unpriced_count} dòng
                </div>
              )}
            </div>
          </div>

          <div className="small text-muted mt-1 mb-2">
            {supplier.branches.map((b) => `${b.name} (${b.item_count})`).join(' · ')}
          </div>

          {/* Đơn CÒN ĐANG MỞ của NCC này — chống đặt trùng, dùng đúng PurchaseOrderStatus */}
          {supplier.open_orders.length > 0 && (
            <Alert variant="info" className="py-2 px-3 mb-2 small">
              <div className="fw-bold mb-1">NCC này đang có đơn mở — kiểm trước khi đặt lại:</div>
              {supplier.open_orders.map((po) => (
                <div key={po.id} className="d-flex justify-content-between gap-2">
                  <span
                    role="button"
                    className="text-decoration-underline"
                    onClick={() => navigate(`/inventory/purchase-order/${po.id}`)}
                  >
                    {po.order_number} · {po.branch_name} · {po.item_count} dòng · {fmtDate(po.created_at)}
                  </span>
                  <Badge bg={(ORDER_STATUS[po.status] || {}).bg || 'secondary'}>
                    {(ORDER_STATUS[po.status] || {}).text || po.status}
                  </Badge>
                </div>
              ))}
            </Alert>
          )}

          {renderChannelActions(supplier)}

          <Button
            size="sm"
            variant="outline-secondary"
            className="w-100"
            onClick={() => setExpanded((p) => ({ ...p, [supplier.supplier_id]: !open }))}
          >
            {open ? 'Ẩn danh sách NVL' : `Xem ${supplier.item_count} NVL cần đặt`}
          </Button>

          {open && (
            <div className="mt-2">
              {supplier.items.map((it) => {
                const key = `${supplier.supplier_id}|${it.branch_id}|${it.material_id}`;
                return (
                  <div key={key} className="border-top pt-2 mt-2">
                    <div className="d-flex justify-content-between gap-2">
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">{it.material_name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {it.branch_name} · tồn {fmtQty(it.current_stock)} {it.unit}
                          {' · ngưỡng '}{fmtQty(it.min_stock_threshold)}
                          {it.effective_par_level !== null
                            ? ` · định mức ${fmtQty(it.effective_par_level)}`
                            : ' · CHƯA có định mức'}
                        </div>
                        {it.other_supplier_count > 0 && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            còn {it.other_supplier_count} NCC khác cũng bán món này
                          </div>
                        )}
                        {it.pending_order && (
                          <div className="text-info" style={{ fontSize: '0.72rem' }}>
                            {it.pending_order_supplier_matched
                              ? `đã nằm trong đơn ${it.pending_order.order_number} — đừng đặt lại`
                              : `NGHI trùng: đơn ${it.pending_order.order_number} có món này nhưng dòng đơn chưa gán NCC`}
                          </div>
                        )}
                      </div>
                      <div style={{ width: 108 }}>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min="0"
                            step="any"
                            inputMode="decimal"
                            value={qtyOf(key, it.suggested_packages)}
                            placeholder="—"
                            onChange={(e) => setQtyDraft((p) => ({ ...p, [key]: e.target.value }))}
                          />
                          <InputGroup.Text style={{ fontSize: '0.7rem' }}>
                            {pkgLabel(it.package || it.default_package, it.unit)}
                          </InputGroup.Text>
                        </InputGroup>
                        <div className="text-end text-muted" style={{ fontSize: '0.7rem' }}>
                          {it.default_price !== null ? fmtMoney(it.default_price) : 'chưa có giá'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Đánh dấu "Đã đặt" thủ công — cho kênh gọi điện / kênh khác, và cho cả NCC chưa
                  khai kênh (gọi ngoài hệ thống rồi vẫn phải ghi nhận được, nếu không màn này
                  báo thiếu mãi). Tạo ĐƠN THẬT ở trạng thái CONFIRMED, không phải ô tick riêng. */}
              {(hasPhoneChannel || noChannel) && supplier.branches.map((b) => (
                <Button
                  key={b.id}
                  size="sm"
                  variant="outline-success"
                  className="w-100 mt-2"
                  disabled={busySupplier === `${supplier.supplier_id}|${b.id}`}
                  onClick={() => markOrdered(supplier, b)}
                >
                  {busySupplier === `${supplier.supplier_id}|${b.id}`
                    ? <Spinner size="sm" animation="border" />
                    : `✅ Đánh dấu ĐÃ ĐẶT — ${b.name} (${b.item_count} món)`}
                </Button>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <PageLayout>
      <div className="container-fluid px-2 px-md-3 py-3">
        <h4 className="mb-1">NCC đang cần đặt hàng</h4>
        <p className="text-muted small mb-3">
          NVL dưới ngưỡng tối thiểu, gom <strong>theo nhà cung cấp</strong>. Mỗi NCC thao tác theo
          đúng <strong>cách đặt</strong> của họ — trang này không tự đặt hàng hộ.
        </p>
        <div className="d-flex gap-2 mb-2">
          <Form.Select
            size="sm"
            value={branchId}
            onChange={(e) => onChangeBranch(e.target.value)}
          >
            <option value="">Tất cả chi nhánh</option>
            {branchOptions.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Form.Select>
          <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : '⟳'}
          </Button>
        </div>

        <Form.Control
          size="sm"
          className="mb-2"
          placeholder="Tìm theo tên NCC hoặc tên NVL…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && <Alert variant="danger" className="py-2 px-3">{error}</Alert>}

        {data && (
          <>
            <Alert variant="secondary" className="py-2 px-3 small mb-2">
              {data.quantity_source}
            </Alert>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge bg="dark">{data.summary.supplier_count} NCC</Badge>
              <Badge bg="danger">{data.summary.need_count} món cần đặt</Badge>
              {data.summary.unassigned_count > 0 && (
                <Badge bg="warning" text="dark">
                  {data.summary.unassigned_count} món chưa có NCC
                </Badge>
              )}
              {data.summary.no_channel_supplier_count > 0 && (
                <Badge bg="warning" text="dark">
                  {data.summary.no_channel_supplier_count} NCC chưa khai cách đặt
                </Badge>
              )}
            </div>

            {/* NHÓM RIÊNG, LUÔN HIỆN TRÊN ĐẦU: NVL cần mua mà chưa gắn NCC nào. Ẩn đi thì người
                dùng đặt xong tưởng đã đủ, trong khi mấy món này không ai đặt. */}
            {unassigned.length > 0 && (
              <Card className="mb-3 border-warning">
                <Card.Body className="p-3">
                  <div className="fw-bold text-warning-emphasis mb-1">
                    ⚠️ {unassigned.length} NVL dưới định mức CHƯA GẮN NHÀ CUNG CẤP NÀO
                  </div>
                  <div className="small text-muted mb-2">
                    Những món này KHÔNG nằm trong thẻ NCC nào ở dưới — chưa ai đặt được cho tới khi
                    gắn NCC.
                  </div>
                  {unassigned.map((it) => (
                    <div
                      key={`${it.branch_id}|${it.material_id}`}
                      className="border-top pt-2 mt-2 d-flex justify-content-between gap-2"
                    >
                      <div>
                        <div className="fw-semibold small">{it.material_name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {it.branch_name} · tồn {fmtQty(it.current_stock)} {it.unit}
                          {' · ngưỡng '}{fmtQty(it.min_stock_threshold)}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="warning"
                        className="flex-shrink-0"
                        onClick={() => navigate('/inventory/suppliers')}
                      >
                        Gắn NCC
                      </Button>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            {suppliers.length === 0 && unassigned.length === 0 && !loading && (
              <Alert variant="success" className="py-2 px-3">
                Không có NVL nào dưới định mức trong phạm vi đang xem.
              </Alert>
            )}

            {suppliers.map(renderSupplier)}
          </>
        )}
      </div>
    </PageLayout>
  );
}
